import { useEffect, useState } from 'react'
import { ChevronsUpDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useHaloUser, useHaloUsers } from './hooks'

interface HaloUserComboboxProps {
  value: number | null
  onChange: (id: number | null) => void
  placeholder?: string
  /** When true, the combobox displays even inactive users (eg. on detail pages where the
   *  customer's old assignee may have been deactivated). Default false. */
  showInactiveByDefault?: boolean
  className?: string
}

export function HaloUserCombobox({
  value,
  onChange,
  placeholder = 'Wybierz opiekuna...',
  showInactiveByDefault = false,
  className,
}: HaloUserComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce 250ms - jeden user co ~150ms typing.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250)
    return () => clearTimeout(id)
  }, [search])

  const selected = useHaloUser(value)
  const list = useHaloUsers({
    active: !showInactiveByDefault,
    search: debouncedSearch,
    offset: 0,
    limit: 25,
  })

  const handleSelect = (id: number) => {
    onChange(id)
    setOpen(false)
    setSearch('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  const buttonLabel = (() => {
    if (value == null) return placeholder
    if (selected.isLoading) return 'Ładowanie...'
    if (selected.isError) return `#${value} (nie odnaleziono)`
    if (selected.data) {
      const name = selected.data.fullName
      return selected.data.isActive ? name : `${name} (nieaktywny)`
    }
    return placeholder
  })()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            value == null && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{buttonLabel}</span>
          <span className="flex items-center gap-1">
            {value != null && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClear(e as unknown as React.MouseEvent)
                }}
                className="rounded p-0.5 hover:bg-muted"
                aria-label="Wyczyść opiekuna"
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronsUpDown className="size-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <div className="border-b p-2">
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po imieniu/nazwisku..."
            className="h-8"
          />
        </div>
        <ul className="max-h-72 overflow-y-auto p-1">
          {list.isLoading ? (
            <li className="p-2"><Skeleton className="h-4 w-full" /></li>
          ) : list.isError ? (
            <li className="px-3 py-2 text-sm text-destructive">Błąd ładowania listy.</li>
          ) : list.data && list.data.items.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Brak wyników.</li>
          ) : (
            list.data?.items.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(user.id)}
                  className={cn(
                    'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                    user.id === value && 'bg-muted font-medium',
                    !user.isActive && 'text-muted-foreground',
                  )}
                >
                  <span className="truncate">{user.fullName}</span>
                  {!user.isActive && <span className="text-xs">nieaktywny</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
